import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { founder, salon } from '../../data/content';
import { useSite } from '../../contexts/SiteContext';
import { kuurLabel, treatmentDuration, treatmentPrice } from '../../utils/catalog';
import { FormError } from '../shared/FormError';
import { longDate, shortDate, today, weekdayLong } from '../../core/format/dates';
import { isKuurExpired } from '../../core/rules/kuur';
import { ease } from '../../core/format/motion';
import { shopLabels } from '../../data/shopLabels';
import { chipCls, choiceState, inputSandCls } from '../../ui/styles';
import { acquisitionQuestion } from '../../data/acquisition';
import { SourceChips } from '../shared/SourceChips';
import { PerformerChoice } from './PerformerChoice';
import type { BookingControl } from './useBooking';
export const BOOKING_FORM_ID = 'booking-form';
const DETAILS_ID = 'booking-details';

/** Tijden ("09:30") per dagdeel, in volgorde; lege dagdelen vallen weg. */
function dayParts(slots: string[]): {
  label: string;
  slots: string[];
}[] {
  const hour = (t: string) => Number(t.slice(0, 2));
  return [{
    label: 'Ochtend',
    slots: slots.filter(t => hour(t) < 12)
  }, {
    label: 'Middag',
    slots: slots.filter(t => hour(t) >= 12 && hour(t) < 17)
  }, {
    label: 'Avond',
    slots: slots.filter(t => hour(t) >= 17)
  }].filter(p => p.slots.length > 0);
}
const legend = 'type-name text-ink';
const chip = chipCls;
const input = inputSandCls;
const linkCls = 'text-ink underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay';
const visitOptions = [{
  value: true,
  label: 'Eerste keer'
}, {
  value: false,
  label: 'Ik ben al klant'
}];

/** De aanvraag zelf: wie, welke behandeling, welke dag en tijd. */
export function BookingForm({
  b
}: {
  b: BookingControl;
}) {
  const {
    policy
  } = useSite();
  const reduce = useReducedMotion();
  const {
    account,
    selected,
    session,
    course
  } = b;

  // Tijd gekozen en nog geen gegevens: de volgende stap in beeld schuiven (op de telefoon staat die
  // onder de vouw). Geen focus verplaatsen: dan springt het toetsenbord ongevraagd open.
  const pickTime = (t: string) => {
    b.setTime(t);
    if (account || b.name) return;
    document.getElementById(DETAILS_ID)?.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'start'
    });
  };
  return <form id={BOOKING_FORM_ID} onSubmit={b.submit} aria-busy={b.submitting} className="space-y-9" noValidate data-id="element-8402" data-mp="1v53z1kpcifln">
      <FormError message={b.error} data-id="element-8403" data-mp="xkhm9snz1s3g" />

      {account ? <div className="bg-sand px-4 py-3.5 text-[14px] leading-relaxed" data-id="element-8404" data-mp="12gyaemv686gj">
          <p className="text-ink" data-id="element-8405" data-mp="17gufyqjio5y7">
            Je boekt als {account.customer.firstName} {account.customer.lastName}
          </p>
          <p className="text-muted" data-id="element-8406" data-mp="1cg7a7wcpwfat">
            {account.customer.email}
            {b.knownFirstVisit && ' · je eerste bezoek begint met een uitgebreide intake'}
          </p>
        </div> : <fieldset data-id="element-8407" data-mp="2nu1fdi9zy2br">
          <legend className={legend} data-id="element-8408" data-mp="2ntj9rv1g2br6">Ben je al eens geweest?</legend>
          <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-8409" data-mp="c80muc7195yt">
            {visitOptions.map(o => <button key={o.label} type="button" aria-pressed={b.firstVisit === o.value} onClick={() => b.setFirstVisit(o.value)} className={chip(b.firstVisit === o.value)} data-id="element-8410" data-mp="78e981nalnph">
                {o.label}
              </button>)}
          </div>
          <AnimatePresence initial={false} data-id="element-8411" data-mp="3tm4kd7ciovik">
            {b.firstVisit && <motion.p initial={reduce ? {
          opacity: 0
        } : {
          height: 0,
          opacity: 0
        }} animate={reduce ? {
          opacity: 1
        } : {
          height: 'auto',
          opacity: 1
        }} exit={reduce ? {
          opacity: 0
        } : {
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease
        }} className="overflow-hidden" data-id="element-8412" data-mp="3oqpasrljllry">
                <span className="mt-3 block bg-sand px-4 py-3 text-[14px] leading-relaxed text-muted" data-id="element-8413" data-mp="1dw1m9dqup83f">
                  Welkom! Je eerste behandeling start met een uitgebreide intake van je huid en routine. Deze reken je vooraf online af; daarna betaal je gewoon in de
                  salon.
                </span>
              </motion.p>}
          </AnimatePresence>
        </fieldset>}

      <fieldset data-id="element-8414" data-mp="3sr0hdkr71fiu">
        <legend className={legend} data-id="element-8415" data-mp="3srk0v5whsbyz">Behandeling</legend>
        <label htmlFor="booking-treatment" className="sr-only" data-id="element-8416" data-mp="1kzcjtxtni5n">
          Kies een behandeling
        </label>
        <select id="booking-treatment" value={b.treatment} onChange={e => b.setTreatment(e.target.value)} aria-describedby={selected ? 'booking-treatment-note' : undefined} className={`mt-3 ${input}`} data-id="element-8417" data-mp="3j1qssevk6ezh">
          {b.treatments.map(t => <option key={t.slug} value={t.slug} data-id="element-8418" data-mp="3f1glmyw15ij1">
              {t.name} — {treatmentDuration(t)}, {treatmentPrice(t)}
            </option>)}
        </select>
        {selected && <p id="booking-treatment-note" className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-8419" data-mp="352hbfbvm9etx">
            {selected.tagline}
            {selected.kuur && !session ? ` · kuur: ${kuurLabel(selected)}` : ''}
          </p>}
        {session && course && <p className="mt-2 text-[14px] text-clay" data-id="element-8420" data-mp="2velpt3kkcuo8">
            Dit wordt sessie {session} van {course.course.total} van je kuur.
            {course.course.prepaid && (isKuurExpired(course.course, today()) ? ' Je vooruitbetaalde kuur is verlopen; deze sessie reken je in de salon af.' : ' Al betaald, je hoeft niets meer af te rekenen.')}
          </p>}
      </fieldset>

      <PerformerChoice b={b} data-id="element-8421" data-mp="30blw9flbrzfv" />

      <fieldset data-id="element-8422" data-mp="3e4tmb2trcmvg">
        <legend className={legend} data-id="element-8423" data-mp="3e5cx7zkzqazz">Dag</legend>
        {b.slotsLoading ? <p className="mt-3 text-[14px] text-muted" role="status" data-id="element-8424" data-mp="396a33a6jtp6s">
            Vrije tijden ophalen…
          </p> : b.days.length === 0 ? <p className="mt-3 text-[14px] text-muted" data-id="element-8425" data-mp="3o3srgbfbe0dp">
            De komende weken is er geen plek.{' '}
            <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={linkCls} data-id="element-8426" data-mp="3j60vn6bjtu48">
              App {founder.firstName}
              <span className="sr-only" data-id="element-8427" data-mp="3v6h49cxx8q1h">{shopLabels.viaWhatsApp}</span>
            </a>{' '}
            voor een plekje tussendoor.
          </p> : <>
          {b.approximate && <p className="mt-3 text-[13px] text-muted" role="status" data-id="element-8428" data-mp="8qmkjbzurv1s">
              De agenda laadde even niet. Je ziet de openingstijden; is een tijd toch bezet, dan hoor je dat meteen en kies je een andere.
            </p>}
          <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-8429" data-mp="hqjgzzshhy7e">
            {b.days.map((d, i) => {
            const active = b.dayIndex === i;
            const full = b.dayFull(i);
            const who = b.dayStaff(i);
            return <button key={d.toISOString()} type="button" aria-pressed={active} aria-label={`${longDate(d)}${who ? `, met ${who}` : ''}${full ? ', vol' : ''}`} disabled={full} onClick={() => b.pickDay(i)}
            // Vol: zichtbaar gezegd en gestippeld, niet alleen vager (dat was op zand slecht leesbaar).
            className={`rounded-sm px-2 py-3 text-center disabled:cursor-not-allowed disabled:border-dashed disabled:bg-transparent disabled:text-muted ${choiceState(active)}`} data-id="element-8430" data-mp="rneau5dtbafc" data-mpi={i}>
                  <span className="block text-[13px] capitalize" aria-hidden="true" data-id="element-8431" data-mp="34x06k7qdk5ga">
                    {weekdayLong(d)}
                  </span>
                  <span className="mt-0.5 block type-name" aria-hidden="true" data-id="element-8432" data-mp="2zy5cuaqvcwhe">
                    {shortDate(d)}
                  </span>
                  {full ? <span className="mt-0.5 block text-[12px]" aria-hidden="true" data-id="element-8433" data-mp="2py64adug964j">
                      Vol
                    </span> : who && <span className="mt-0.5 block truncate text-[12px] opacity-75" aria-hidden="true" data-id="element-8434" data-mp="1ejrhch7lzpd0">
                        met {who}
                      </span>}
                </button>;
          })}
          </div>
          </>}
      </fieldset>

      <fieldset data-id="element-8435" data-mp="1v8jjiayi5x0s">
        <legend className={legend} data-id="element-8436" data-mp="1v92uf7pqjl5b">Tijdstip</legend>
        {/* Dag gekozen maar geen tijden (bijv. vandaag na sluitingstijd): dat zeggen, niet "Kies eerst een dag". */}
        {!b.day ? <p className="mt-3 text-[14px] text-muted" data-id="element-8437" data-mp="2mz7m7rftragk">Kies eerst een dag.</p> : b.slots.length === 0 ? <p className="mt-3 text-[14px] text-muted" role="status" data-id="element-8438" data-mp="2rwzbedp34ih2">
            Op deze dag is er geen tijd meer vrij. Kies een andere dag.
          </p> :
      // Per dagdeel: op een drukke dag is een lange rij tijden op de telefoon slecht te scannen.
      <div className="mt-3 space-y-4" data-id="element-8439" data-mp="35wh5n8l5gnxt">
            {dayParts(b.slots).map(part => <div key={part.label} role="group" aria-labelledby={`slots-${part.label}`} data-id="element-8440" data-mp="3aoiiavm8cgod">
                <p id={`slots-${part.label}`} className="mb-2 text-[13px] text-muted" data-id="element-8441" data-mp="z8hwkojixswv">
                  {part.label}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-8442" data-mp="u9wuskomj16b">
                  {part.slots.map(t => <button key={t} type="button" aria-pressed={b.time === t} onClick={() => pickTime(t)} className={chip(b.time === t)} data-id="element-8443" data-mp="18yb6ll40pzz0">
                      {t}
                    </button>)}
                </div>
              </div>)}
          </div>}
      </fieldset>

      {!account && <fieldset id={DETAILS_ID} className="scroll-mt-4 space-y-3" data-id="element-8444" data-mp="q8lfp93dlycd">
          <legend className={legend} data-id="element-8445" data-mp="q9wjh8wtn63h">Jouw gegevens</legend>
          {/* Zichtbare labels: placeholders verdwijnen zodra je typt of de browser invult. */}
          <div className="mt-3" data-id="element-8446" data-mp="21myyhzj9vnlj">
            <label htmlFor="booking-name" className="mb-1.5 block text-[14px] text-ink" data-id="element-8447" data-mp="21m724mhuplu7">
              Naam
            </label>
            <input id="booking-name" value={b.name} autoComplete="name" onChange={e => b.setName(e.target.value)} required className={input} data-id="element-8448" data-mp="1wr01jnjvox9i" />
          </div>
          <div data-id="element-8449" data-mp="1aalg0l5k097t">
            <label htmlFor="booking-email" className="mb-1.5 block text-[14px] text-ink" data-id="element-8450" data-mp="1aa28lydj5381">
              E-mailadres
            </label>
            <input id="booking-email" type="email" inputMode="email" value={b.email} autoComplete="email" onChange={e => b.setEmail(e.target.value)} onBlur={() => b.setEmailTouched(true)} required aria-invalid={b.emailInvalid || undefined} aria-describedby={b.emailInvalid ? 'booking-email-error' : undefined} className={input} data-id="element-8451" data-mp="10jqtdfoeb7lu" />
            {b.emailInvalid && <p id="booking-email-error" className="mt-1.5 text-[13px] text-clay" data-id="element-8452" data-mp="2gwo2eipx4ut6">
                Dit e-mailadres lijkt niet te kloppen. Bijvoorbeeld naam@voorbeeld.nl.
              </p>}
          </div>
          {b.askSource && <div className="pt-3" data-id="element-8453" data-mp="2jgawlwz1d36s">
              <SourceChips id="booking-source" value={b.source} onChange={b.setSource} legend={<>
                    {acquisitionQuestion} <span className="text-muted" data-id="element-8455" data-mp="2y7b86av2xd75">(hoeft niet)</span>
                  </>} legendClassName="text-[14px] text-ink" data-id="element-8454" data-mp="2ogghmuasvrfw" />
            </div>}
          <p className="text-[13px] leading-relaxed text-muted" data-id="element-8456" data-mp="25jfbh65iflad">
            Je aanvraag is vrijblijvend. {founder.firstName} bevestigt persoonlijk binnen {policy.responseHours} uur en stemt de behandeling vooraf met je af. Liever
            mailen?{' '}
            <a href={`mailto:${salon.email}`} className={linkCls} data-id="element-8457" data-mp="3lwm9puk97bmc">
              {salon.email}
            </a>
          </p>
        </fieldset>}
    </form>;
}